import React, { createContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import useAuthenticationService from '../api/services/authenticationService';
import toast from 'react-hot-toast';
import { getUserFromCookies, removeUserCookies } from '../utils/Cookie-utils';
import {
  clearSessionTimeoutMonitor,
  getAccessToken,
  isIdleTimeoutExceeded,
  logoutToLogin,
  refreshAccessToken,
  startSessionTimeoutMonitor,
} from '../utils/session-utils';


// Create AuthContext
export const AuthContext = createContext({
  isAuthenticated: false,
  authReady: false,
  user: null,
  signIn: () => Promise.resolve(),
  signUp: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  requestPasswordReset: () => Promise.resolve(),
  verifyResetCode: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
  refreshUser: () => Promise.resolve(),
});

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const {
    signIn: signInService,
    signUp: signUpService,
    fetchUserDetails,
    logout: signOutService,
    requestPasswordResetService,
    verifyResetCodeService,
    resetPasswordService,
  } = useAuthenticationService();

  useEffect(() => {
    let isMounted = true;

    const token = getAccessToken();
    if (token) {
      if (isIdleTimeoutExceeded()) {
        logoutToLogin({
          message: 'You have been logged out due to 15 minutes of inactivity.',
        });
        if (isMounted) setAuthReady(true);
      } else {
        startSessionTimeoutMonitor();
        fetchUserDetails()
          .then((context) => {
            if (context && isMounted) {
              setUser({ data: context });
              setIsAuthenticated(true);
              if (context.loginForFirstTime === true) {
                navigate('/change-password-first-login', { replace: true });
              }
            }
          })
          .catch(() => {
            // If cache-backed context fetch fails, the next authenticated request will retry.
            if (isMounted) {
              setIsAuthenticated(false);
              setUser(null);
            }
          })
          .finally(() => {
            if (isMounted) {
              setAuthReady(true);
            }
          });
        refreshAccessToken().catch(() => {
          // Token refresh will be retried on the next API call.
        });
      }
    } else {
      setAuthReady(true);
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      const currentToken = getAccessToken();
      if (!currentToken) return;

      if (isIdleTimeoutExceeded()) {
        logoutToLogin({
          message: 'You have been logged out due to 15 minutes of inactivity.',
        });
        return;
      }

      startSessionTimeoutMonitor();
      refreshAccessToken().catch(() => {
        // Token refresh will be retried on the next API call.
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      clearSessionTimeoutMonitor();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const refreshUser = useCallback(async () => {
    const context = await fetchUserDetails();
    if (context) {
      setUser({ data: context });
    }
    return context;
  }, [fetchUserDetails]);

  const signIn = useCallback(async (email, phoneNumber, password) => {
    try {
      const response = await signInService(email, phoneNumber, password);
      if (response.code === '0') {
        const resolvedUser = response.data?.user ? { data: response.data.user } : getUserFromCookies();
        setIsAuthenticated(true);
        setUser(resolvedUser);
        setAuthReady(true);
        startSessionTimeoutMonitor({ resetActivity: true });
        toast.success(response.message);
        if (resolvedUser?.data?.loginForFirstTime === true) {
          navigate('/change-password-first-login', { replace: true });
        } else {
          navigate('/dashboard');
        }
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      throw new Error(error.message);
    }
  }, [navigate, signInService]);

  const signUp = useCallback(async (userData) => {
    try {
      const response = await signUpService(userData);
      if (response.code === '0') {
        
        navigate('/login');
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error('AuthProvider SignUp Error:', error.message);
      toast.error(error.message );
    }
  }, [navigate, signUpService]);



  const signOut = useCallback(async () => {
    try {
      clearSessionTimeoutMonitor();
      await signOutService();
      setIsAuthenticated(false);
      setAuthReady(true);
      setUser(null);
      removeUserCookies();
      navigate('/');
    } catch (error) {
      throw new Error(error.message);
    }
  }, [navigate, signOutService]);
  


  const requestPasswordReset = useCallback(async (email) => {
    try {
      await requestPasswordResetService(email);
      toast.success("Code is sent to your email account");
    } catch (error) {
      toast.error("Error in sending code to your email account");
      console.error(error.message);
    }
  }, [requestPasswordResetService]);

  const verifyResetCode = useCallback(async (email, code) => {
    try {
      await verifyResetCodeService(email, code);
      toast.success("Code is successfully verified");
    } catch (error) {
      toast.error("Error in verifying code");
      console.error(error.message);
    }
  }, [verifyResetCodeService]);

  const resetPassword = useCallback(async (email, newPassword, confirmPassword) => {
    try {
      await resetPasswordService(email, newPassword, confirmPassword); 
      toast.success("New password is saved successfully");
      navigate('/');
    } catch (error) {
      toast.error("Error in saving new password");
      console.error(error.message);
    }
  }, [resetPasswordService, navigate]);


  return (
    <AuthContext.Provider value={{ isAuthenticated, authReady, user, signIn, signUp, signOut, requestPasswordReset, verifyResetCode, resetPassword, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
